import { useEffect, useState } from "react";

/** The lists that teach a swipe; each keeps its own "seen" day. */
export type SwipeHintList = "records" | "issues";

/**
 * Device-local marker: the local day the peek last played in a list. One key
 * per list, so the photos list's nudge does not spend the issues list's. Read
 * and written the way `otus:theme` is (see `src/lib/theme.ts`) — a blocked
 * store never throws into render. The bare `otus:swipe-hint-seen` key of the
 * once-per-device hint is a different key and is ignored.
 */
export function swipeHintStorageKey(list: SwipeHintList): string {
  return `otus:swipe-hint-seen:${list}`;
}
/** How long the nudge runs. Keep in step with `.swipe-hint-nudge` in index.css. */
export const SWIPE_HINT_DURATION_MS = 600;
/** A beat after the row is on screen, so the nudge isn't lost in the page settling. */
export const SWIPE_HINT_DELAY_MS = 400;
/** How much of the row must be showing before it counts as on screen. */
const VISIBLE_RATIO = 0.5;

const COARSE_POINTER = "(pointer: coarse)";
const REDUCED_MOTION = "(prefers-reduced-motion: reduce)";

/**
 * The device's local calendar day as `YYYY-MM-DD`: the unit the hint repeats
 * on. Local rather than UTC, so "a new day" turns over at the person's midnight.
 */
export function swipeHintDay(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

// Held in memory too, so a session whose storage is blocked (private mode)
// plays each list's hint once a day rather than on every mount.
const seenThisSession = new Map<SwipeHintList, string>();

function hasSeenHint(list: SwipeHintList): boolean {
  const today = swipeHintDay();
  if (seenThisSession.get(list) === today) return true;
  try {
    return localStorage.getItem(swipeHintStorageKey(list)) === today;
  } catch {
    return false;
  }
}

function markHintSeen(list: SwipeHintList): void {
  const today = swipeHintDay();
  try {
    localStorage.setItem(swipeHintStorageKey(list), today);
  } catch {
    seenThisSession.set(list, today);
  }
}

/** Unlike `useMediaQuery`, no `matchMedia` means no match: a hint is opt-in. */
function matches(query: string): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia(query).matches
  );
}

/** Clears the in-memory "seen" copy between tests. */
export function __resetSwipeHintForTesting(): void {
  seenThisSession.clear();
}

interface UseSwipeHintOptions {
  /**
   * Whether this row is the one that plays the hint: the list's first
   * swipeable row, and only while its swipe is on. Everything else — selection
   * mode, desktop, no permission — arrives here as false.
   */
  enabled: boolean;
  /** The list the row belongs to, whose "seen" day this row reads and writes. */
  list: SwipeHintList;
}

/**
 * The daily peek that tells someone a list row swipes: the row nudges left and
 * back, once per list per local day. See [[photo-list-item.spec]] US-006 and
 * [[issue-item.spec]] US-004.
 *
 * The caller attaches `ref` to the element that carries the swipe's transform
 * and adds the `swipe-hint-nudge` class (index.css) while `isHinting` is true.
 * The CSS owns the motion and switches it off under `prefers-reduced-motion`;
 * this hook owns whether and when: a coarse pointer, no reduced-motion
 * preference, the row at least half on screen, and a list that has not played
 * it today. The pointer and motion preferences are read once, when the row is
 * ready. The day is read again at the moment of playing, so two rows waiting at
 * once cannot both nudge, and a row that waited past midnight asks about the
 * new day.
 *
 * A finger on the row ends the nudge at once, leaving the swipe to the drag.
 * The day is stored when the nudge starts, not when it ends: one that began has
 * been seen, and replaying it on the next visit the same day would nag; one
 * that never began (interrupted, or the row stopped being the swipeable one) is
 * still owed.
 */
export function useSwipeHint({ enabled, list }: UseSwipeHintOptions): {
  ref: (node: HTMLElement | null) => void;
  isHinting: boolean;
} {
  // State rather than a ref object: the effect must re-run when the element
  // mounts or is swapped, and a ref's `.current` change would not trigger it.
  const [node, setNode] = useState<HTMLElement | null>(null);
  const [isHinting, setIsHinting] = useState(false);

  useEffect(() => {
    if (!enabled || !node) return;
    if (hasSeenHint(list)) return;
    if (!matches(COARSE_POINTER) || matches(REDUCED_MOTION)) return;

    let delayTimer: ReturnType<typeof setTimeout> | undefined;
    let endTimer: ReturnType<typeof setTimeout> | undefined;
    let observer: IntersectionObserver | undefined;

    const play = () => {
      observer?.disconnect();
      // Another row may have spent today's hint while this one waited its turn.
      if (hasSeenHint(list)) return;
      markHintSeen(list);
      setIsHinting(true);
      endTimer = setTimeout(() => setIsHinting(false), SWIPE_HINT_DURATION_MS);
    };
    const schedule = () => {
      delayTimer = setTimeout(play, SWIPE_HINT_DELAY_MS);
    };
    const stop = () => {
      clearTimeout(delayTimer);
      clearTimeout(endTimer);
      observer?.disconnect();
      setIsHinting(false);
    };

    if (typeof IntersectionObserver === "undefined") {
      schedule();
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[entries.length - 1];
          clearTimeout(delayTimer);
          if (entry.isIntersecting && entry.intersectionRatio >= VISIBLE_RATIO) {
            schedule();
          }
        },
        { threshold: VISIBLE_RATIO },
      );
      observer.observe(node);
    }

    node.addEventListener("touchstart", stop, { passive: true });
    return () => {
      node.removeEventListener("touchstart", stop);
      stop();
    };
  }, [enabled, node, list]);

  return { ref: setNode, isHinting };
}
